import React, { useContext, useMemo } from 'react';
import { Tooltip, Skeleton } from '@mui/material';
import PropTypes from 'prop-types';
import TrendingDownOutlinedIcon from '@mui/icons-material/TrendingDownOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import PrecisionManufacturingOutlinedIcon from '@mui/icons-material/PrecisionManufacturingOutlined';
import ShowChartOutlinedIcon from '@mui/icons-material/ShowChartOutlined';
import { PermissionsContext } from '../../../../Contexts/Permissions.js';
import Constants from '../../../../assests/Constants/constants.js';
import FinanceStatCard from './FinanceStatCard';
import './PanelsCards.css';

const ActionCards = ({
  direction,
  fetchLoading,
  client,
  incomesAmount,
  expensesAmount,
  purchaseOfEquipmentExpensesAmount,
  profitAmount,
  viewDocumentDetails,
  viewSortCodeReport,
  t,
  beautifyNumber,
  startDate,
  endDate,
  isMonthly,
  startYearDate,
  endYearDate,
}) => {
  const { clientPermissionsState, userTypeContext } = useContext(PermissionsContext);

  const isClient = useMemo(() => {
    return userTypeContext?.toString() === Constants.UserType.Client;
  }, [userTypeContext]);

  // VatAdvancesSection already renders the Total Summary table (incomes, expenses,
  // profit, equipment). These cards are only the fallback when that section is hidden.
  const showFallbackCards = Boolean(clientPermissionsState?.CannotSeeVat && isClient);

  if (!showFallbackCards) {
    return null;
  }

  if (fetchLoading || !client) {
    return (
      <div style={{ display: 'flex', gap: '16px' }}>
        {[...Array(4)].map((_, idx) => (
          <Skeleton
            key={idx}
            variant="rounded"
            width={180}
            height={100}
            sx={{ borderRadius: '8px' }}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="va-kpi-grid" dir={direction}>
      <Tooltip title={t('NavigateToIncomes')}>
        <span>
          <FinanceStatCard
            layout="kpi"
            variant="income"
            label={t('Incomes')}
            value={`${beautifyNumber(incomesAmount)} ₪`}
            onClick={() => viewDocumentDetails('Incomes')}
            icon={<TrendingDownOutlinedIcon />}
          />
        </span>
      </Tooltip>
      <Tooltip title={t('NavigateToExpenses')}>
        <span>
          <FinanceStatCard
            layout="kpi"
            variant="expense"
            label={t('Expenses')}
            value={`${beautifyNumber(expensesAmount)} ₪`}
            onClick={() => viewDocumentDetails('Expenses')}
            icon={<TrendingUpOutlinedIcon />}
          />
        </span>
      </Tooltip>
      <FinanceStatCard
        layout="kpi"
        variant="profit"
        label={t('financialProfit')}
        value={`${beautifyNumber(profitAmount)} ₪`}
        valueClassName={Number(profitAmount) < 0 ? 'is-negative' : ''}
        icon={<ShowChartOutlinedIcon />}
      />
      <Tooltip title={t('NavigateToPurchaseOfEquipment')}>
        <span>
          <FinanceStatCard
            layout="kpi"
            variant="equipment"
            label={t('PurchaseExpenses')}
            value={`${beautifyNumber(purchaseOfEquipmentExpensesAmount)} ₪`}
            onClick={() => viewSortCodeReport('רכישת ציוד', {
              startDate,
              endDate,
              isMonthly,
              startYearDate,
              endYearDate,
            })}
            icon={<PrecisionManufacturingOutlinedIcon />}
          />
        </span>
      </Tooltip>
    </div>
  );
};
ActionCards.propTypes = {
    fetchLoading: PropTypes.bool.isRequired,
    client: PropTypes.object,
    incomesAmount: PropTypes.number,
    expensesAmount: PropTypes.number,
    purchaseOfEquipmentExpensesAmount: PropTypes.number,
    profitAmount: PropTypes.number,
    viewDocumentDetails: PropTypes.func.isRequired,
    viewSortCodeReport: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
    beautifyNumber: PropTypes.func.isRequired,
    direction: PropTypes.string,
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date),
    isMonthly: PropTypes.bool,
    startYearDate: PropTypes.instanceOf(Date),
    endYearDate: PropTypes.instanceOf(Date),
};
export default ActionCards;
